/**
 * Flashcard Prompt Configuration
 * File này chứa prompt template để tạo flashcards từ PDF
 * Bạn có thể tinh chỉnh prompt này để cải thiện chất lượng flashcards
 */

export interface FlashcardPromptConfig {
  systemPrompt: string
  mainPrompt: (params: {
    count: number
    keywords: string[]
    content: string
  }) => string
  exampleResponse: string
}

export const flashcardPromptConfig: FlashcardPromptConfig = {
  // System prompt - Vai trò của AI
  systemPrompt: `Bạn là một chuyên gia soạn thảo tài liệu giáo dục và là một nhà vật lí có kiến thức sâu rộng, chuyên tạo flashcards học tập chất lượng cao.`,

  // Main prompt - Template chính
  mainPrompt: ({ count, keywords, content }) => `
**Nhiệm vụ:** Phân tích kỹ lưỡng tài liệu Vật lí được cung cấp và tạo ra ${count} flashcards học tập theo các yêu cầu và định dạng nghiêm ngặt dưới đây.

**Đầu vào:**
Tài liệu Vật lí:
\`\`\`
${content}
\`\`\`

**Từ khóa trọng tâm:** ${keywords.length > 0 ? keywords.join(', ') : 'Không xác định'}

**Mục tiêu:** Tạo flashcards giúp học sinh THPT hoặc sinh viên năm nhất củng cố kiến thức Vật lí một cách hiệu quả.

**QUY TẮC & RÀNG BUỘC QUAN TRỌNG:**
1.  **Nguồn duy nhất**: CHỈ sử dụng thông tin và kiến thức có trong tài liệu được cung cấp.
2.  **Không thêm kiến thức ngoài**: TUYỆT ĐỐI KHÔNG thêm bất kỳ thông tin, kiến thức hay ví dụ nào không có trong tài liệu.
3.  **Tính chính xác**: Đảm bảo mọi thông tin trên flashcard đều chính xác và trung thực với tài liệu gốc.
4.  **Trích dẫn nguồn**: Mỗi flashcard PHẢI có nguồn trích dẫn là một đoạn văn bản gốc từ tài liệu, có độ dài tối đa 100 ký tự.
5.  **Xử lý thiếu nội dung**: Nếu tài liệu không đủ nội dung để tạo ${count} flashcards, hãy tạo số lượng tối đa có thể và phân bổ độ khó hợp lý nhất.

**YÊU CẦU CẤU TRÚC FLASHCARD:**
Mỗi flashcard phải là một đối tượng JSON với các trường sau:
-   \`"id"\`: Số thứ tự của flashcard (bắt đầu từ 1).
-   \`"front"\`: Một câu hỏi ngắn gọn, rõ ràng (ví dụ: "Đơn vị nhiệt hóa hơi riêng là gì?", "Công thức nhiệt hóa hơi riêng là gì?").
-   \`"back"\`: **CHỈ trả lời bằng TỪ ĐƠN, CỤM TỪ NGẮN GỌN hoặc CÂU NGẮN (tối đa 15 từ)**:
    -   **TUYỆT ĐỐI KHÔNG viết câu văn dài, KHÔNG giải thích**.
    -   **Chỉ viết đáp án trực tiếp**: đơn vị, công thức, giá trị, thuật ngữ, hoặc câu trả lời ngắn gọn.
    -   **VÍ DỤ ĐÚNG (Từ đơn/Cụm từ)**:
        - Front: "Đơn vị nhiệt hóa hơi riêng là gì?" → Back: "J/kg"
        - Front: "Công thức nhiệt hóa hơi riêng?" → Back: "$L = \\frac{Q}{m}$"
        - Front: "Ký hiệu biên độ dao động?" → Back: "$A$"
        - Front: "Năng lượng một vật có được do chuyển động gọi là gì?" → Back: "Động năng"
        - Front: "Đại lượng đặc trưng cho khả năng tích điện gọi là gì?" → Back: "Điện dung"
    -   **VÍ DỤ ĐÚNG (Câu ngắn - khi cần thiết)**:
        - Front: "Định nghĩa dao động điều hòa?" → Back: "Dao động có li độ biến thiên theo quy luật sin hoặc cos theo thời gian"
        - Front: "Điều kiện để cơ năng được bảo toàn trong dao động là gì?" → Back: "Không có lực cản, lực ma sát hoặc bỏ qua mất mát năng lượng"
    -   **VÍ DỤ SAI** (KHÔNG được viết như này):
        - ❌ "Đơn vị nhiệt hóa hơi riêng là J/kg, đây là năng lượng cần thiết để chuyển 1kg chất từ thể lỏng sang thể khí..."
        - ❌ "Công thức $L = \\frac{Q}{m}$ trong đó Q là nhiệt lượng cung cấp và m là khối lượng chất cần chuyển thể..."
        - ❌ "Động năng là dạng năng lượng mà vật có được khi chuyển động, phụ thuộc vào khối lượng và vận tốc của vật..."
    -   **Nguyên tắc**: 
        - Nếu có thể trả lời bằng 1-3 từ → CHỈ viết 1-3 từ
        - Nếu cần câu trả lời → Tối đa 15 từ, KHÔNG giải thích thêm
-   \`"difficulty"\`: Độ khó của flashcard (1, 2, hoặc 3).
    -   \`1\`: Dễ (khái niệm cơ bản, định nghĩa, công thức đơn giản).
    -   \`2\`: Trung bình (nguyên lý, hiện tượng, công thức cần giải thích, bài tập ứng dụng trực tiếp).
    -   \`3\`: Khó (ứng dụng phức tạp, phân tích chuyên sâu, so sánh, suy luận từ nhiều khái niệm).
-   \`"source"\`: Trích đoạn text gốc từ tài liệu (tối đa 100 ký tự).

**YÊU CẦU ĐỊNH DẠNG CÔNG THỨC TOÁN HỌC (QUAN TRỌNG!):**
-   SỬ DỤNG **LaTeX format** cho TẤT CẢ các công thức, ký hiệu toán học và vật lí.
-   **Inline math**: Bọc trong \`$ ... $\` (ví dụ: \`$F = ma$\`, \`$v = \\frac{s}{t}$\`).
-   **Block math**: Bọc trong \`$$ ... $$\` cho công thức độc lập, nổi bật (ví dụ: \`$$E = mc^2$$\`).
-   **Ký tự đặc biệt & Hy Lạp**: Sử dụng \`\\\` cho các ký tự như \`\\omega\`, \`\\pi\`, \`\\theta\`, \`\\alpha\`, \`\\beta\`, \`\\Delta\`, \`\\lambda\`.
-   **Phân số**: \`\\frac{tử}{mẫu}\` (ví dụ: \`$\\frac{1}{2}$\`).
-   **Căn**: \`\\sqrt{x}\` (ví dụ: \`$\\sqrt{2}$\`).
-   **Lũy thừa**: \`x^{2}\` (ví dụ: \`$v^2$\`).
-   **Chỉ số dưới**: \`x_{1}\` (ví dụ: \`$v_{0}$\`).
-   **Vector**: \`\\vec{v}\`, \`\\vec{F}\`.
-   **Đạo hàm**: \`\\frac{dx}{dt}\`.

**VÍ DỤ VỀ CÁC CÔNG THỨC ĐƯỢC ĐỊNH DẠNG ĐÚNG:**
-   "Công thức vận tốc: \`$v = \\frac{s}{t}$\`"
-   "Động năng: \`$E_k = \\frac{1}{2}mv^2$\`"
-   "Dao động điều hòa: \`$x = A\\cos(\\omega t + \\varphi)$\`"
-   "Tần số góc: \`$\\omega = 2\\pi f$\`"
-   "Gia tốc tức thời: \`$\\vec{a} = \\frac{d\\vec{v}}{dt}$\`"

**KHÔNG ĐƯỢC viết các định dạng sau:**
-   "v = s/t" ❌ (phải là \`$v = \\frac{s}{t}$\` ✅)
-   "E = 1/2mv²" ❌ (phải là \`$E = \\frac{1}{2}mv^2$\` ✅)
-   "omega" ❌ (phải là \`$\\omega$\` ✅)
-   "vector v" ❌ (phải là \`$\\vec{v}$\` ✅)

**PHÂN BỐ ĐỘ KHÓ MỤC TIÊU:**
-   **40%** flashcards độ khó 1 (khái niệm cơ bản, định nghĩa).
-   **40%** flashcards độ khó 2 (công thức, nguyên lý, hiện tượng).
-   **20%** flashcards độ khó 3 (ứng dụng, phân tích, so sánh).

**ĐỊNH DẠNG ĐẦU RA CUỐI CÙNG:**
Trả về một mảng JSON chứa tất cả ${count} flashcards đã tạo theo định dạng sau:

{
  "cards": [
    {
      "id": 1,
      "front": "Đơn vị nhiệt hóa hơi riêng là gì?",
      "back": "J/kg",
      "difficulty": 1,
      "source": "Nhiệt hóa hơi riêng có đơn vị là J/kg..."
    },
    {
      "id": 2,
      "front": "Công thức nhiệt hóa hơi riêng?",
      "back": "$L = \\\\frac{Q}{m}$",
      "difficulty": 1,
      "source": "Công thức tính: L = Q/m trong đó..."
    },
    {
      "id": 3,
      "front": "Ký hiệu biên độ dao động?",
      "back": "$A$",
      "difficulty": 1,
      "source": "Biên độ A là giá trị cực đại..."
    },
    {
      "id": 4,
      "front": "Định nghĩa dao động điều hòa?",
      "back": "Dao động có li độ biến thiên theo quy luật sin hoặc cos",
      "difficulty": 2,
      "source": "Dao động điều hòa là dao động mà..."
    }
  ]
}

Hãy tạo ${count} flashcards chất lượng cao với công thức LaTeX đẹp. Trả lời PHẢI là JSON hợp lệ với cấu trúc như trên.
`.trim(),

  // Example response - Mẫu response AI nên trả về
  exampleResponse: `{
  "cards": [
    {
      "id": 1,
      "front": "Đơn vị nhiệt hóa hơi riêng?",
      "back": "J/kg",
      "difficulty": 1,
      "source": "Nhiệt hóa hơi riêng có đơn vị là J/kg..."
    },
    {
      "id": 2,
      "front": "Công thức nhiệt hóa hơi riêng?",
      "back": "$L = \\\\frac{Q}{m}$",
      "difficulty": 1,
      "source": "Công thức tính: L = Q/m trong đó..."
    },
    {
      "id": 3,
      "front": "Năng lượng một vật có được do chuyển động gọi là gì?",
      "back": "Động năng",
      "difficulty": 1,
      "source": "Năng lượng một vật có được do chuyển động..."
    },
    {
      "id": 4,
      "front": "Đại lượng đặc trưng cho khả năng tích điện gọi là gì?",
      "back": "Điện dung",
      "difficulty": 1,
      "source": "Điện dung là đại lượng đặc trưng cho khả năng..."
    },
    {
      "id": 5,
      "front": "Định nghĩa dao động điều hòa?",
      "back": "Dao động có li độ biến thiên theo quy luật sin hoặc cos theo thời gian",
      "difficulty": 2,
      "source": "Dao động điều hòa là dao động mà..."
    },
    {
      "id": 6,
      "front": "Điều kiện để cơ năng được bảo toàn trong dao động là gì?",
      "back": "Không có lực cản, lực ma sát hoặc bỏ qua mất mát năng lượng",
      "difficulty": 2,
      "source": "Cơ năng được bảo toàn khi không có lực cản..."
    }
  ]
}`
}

/**
 * HƯỚNG DẪN TINH CHỈNH PROMPT:
 * 
 * 1. Thay đổi systemPrompt để thay đổi vai trò AI
 * 2. Sửa mainPrompt để thay đổi yêu cầu
 * 3. Cập nhật exampleResponse để guide AI tốt hơn
 * 
 * TIPS TINH CHỈNH:
 * 
 * - Muốn flashcards ngắn gọn hơn:
 *   Thêm: "Mỗi flashcard tối đa 100 từ"
 * 
 * - Muốn nhiều ví dụ hơn:
 *   Thêm: "Mỗi flashcard phải có ít nhất 1 ví dụ cụ thể"
 * 
 * - Muốn focus vào công thức:
 *   Sửa phân bố: "60% công thức, 30% khái niệm, 10% ứng dụng"
 * 
 * - Muốn thêm hints:
 *   Thêm field: "hint": "Gợi ý để nhớ câu trả lời"
 * 
 * - Muốn thêm mnemonics:
 *   Thêm: "Tạo câu ghi nhớ (mnemonic) cho mỗi flashcard"
 * 
 * - Muốn thêm related concepts:
 *   Thêm field: "related": ["khái niệm liên quan"]
 */

